import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Linkedin, Instagram, Youtube, Plus, Trash2, RefreshCw, Sparkles, Edit3,
  ExternalLink, Calendar, FileText, TrendingUp, Heart, MessageCircle, Eye,
  Send, Check, X, AlertCircle, Video, Clock, ChevronDown, ChevronUp, Palette, Save,
  Copy, CalendarClock,
} from 'lucide-react';

// --- datetime helpers (local <-> ISO UTC) ---
const isoToLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const localInputToISO = (local) => {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

const buildDraftText = (d) => {
  if (!d) return '';
  const parts = [];
  const body = (d.text_content || '').trim();
  const hook = (d.hook || '').trim();
  if (hook && !body.startsWith(hook)) parts.push(hook);
  if (body) parts.push(body);
  if (d.hashtags) parts.push(d.hashtags);
  return parts.join('\n\n');
};

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const PLATFORM_ICONS = {
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
};

const PLATFORM_LABELS = {
  linkedin: 'LinkedIn',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

const STATUS_LABELS = {
  draft: 'Entwurf',
  approved: 'Freigegeben',
  scheduled: 'Geplant',
  posted: 'Gepostet',
  rejected: 'Verworfen',
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700',
  approved: 'bg-blue-100 text-blue-700',
  scheduled: 'bg-yellow-100 text-yellow-700',
  posted: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
};


// ── Add-Source-Form ──────────────────────────────────────────────────────────
function AddSourceForm({ onAdded }) {
  const [open, setOpen] = useState(false);
  const [platform, setPlatform] = useState('linkedin');
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!url.trim()) { setError('URL erforderlich'); return; }
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/content/sources`, {
        platform, profile_url: url.trim(), display_name: name.trim() || null,
      }, { headers: authHeaders() });
      setUrl(''); setName(''); setOpen(false);
      onAdded?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSaving(false);
  };

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="w-full bg-[#0a0a0a] text-white py-3 px-5 flex items-center justify-center gap-2 hover:bg-[#1f1f1f] transition-colors uppercase tracking-wider text-sm font-bold"
      style={{ fontFamily: "'Oswald', sans-serif" }}>
      <Plus size={18} /> Quelle hinzufügen
    </button>
  );

  return (
    <div className="bg-white border border-gray-200 p-4 md:p-5 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Neue Quelle
        </h3>
        <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
      </div>

      <div className="flex gap-2">
        {['linkedin', 'instagram', 'youtube'].map(p => {
          const Icon = PLATFORM_ICONS[p];
          return (
            <button key={p} onClick={() => setPlatform(p)}
              className={`flex-1 py-2 px-3 flex items-center justify-center gap-2 border transition-colors text-sm font-bold uppercase tracking-wider ${
                platform === p ? 'border-[#befa0a] bg-[#befa0a]/10 text-[#1f1f1f]' : 'border-gray-200 text-gray-500 hover:border-gray-400'
              }`}
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Icon size={14} /> <span className="hidden sm:inline">{PLATFORM_LABELS[p]}</span>
            </button>
          );
        })}
      </div>

      <input value={url} onChange={e => setUrl(e.target.value)}
        placeholder={platform === 'linkedin' ? 'https://linkedin.com/in/...' : platform === 'instagram' ? 'https://instagram.com/handle/' : 'https://youtube.com/@handle'}
        className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />

      <input value={name} onChange={e => setName(e.target.value)}
        placeholder="Anzeigename (optional)"
        className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />

      {error && <p className="text-red-500 text-xs">{error}</p>}

      <div className="flex gap-2">
        <button onClick={() => setOpen(false)} className="flex-1 py-2 border border-gray-300 text-gray-700 text-sm font-bold uppercase tracking-wider hover:bg-gray-50"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          Abbrechen
        </button>
        <button onClick={submit} disabled={saving}
          className="flex-1 py-2 bg-[#befa0a] text-[#0a0a0a] text-sm font-bold uppercase tracking-wider hover:bg-white disabled:opacity-50"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          {saving ? 'Speichert…' : 'Hinzufügen'}
        </button>
      </div>
    </div>
  );
}


// ── Tab 1: Quellen ────────────────────────────────────────────────────────────
function SourcesTab() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [crawlingId, setCrawlingId] = useState(null);
  const [crawlResult, setCrawlResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/content/sources`, { headers: authHeaders() });
      setSources(data || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Quelle wirklich löschen? Vorhandene Posts bleiben erhalten.')) return;
    await axios.delete(`${API}/content/sources/${id}`, { headers: authHeaders() });
    load();
  };

  const crawl = async (id) => {
    setCrawlingId(id); setCrawlResult(null);
    try {
      const { data } = await axios.post(`${API}/content/sources/${id}/crawl`, {}, { headers: authHeaders() });
      setCrawlResult({ id, ...data });
      load();
    } catch (e) {
      setCrawlResult({ id, error: e.response?.data?.detail || 'Crawl fehlgeschlagen' });
    }
    setCrawlingId(null);
  };

  return (
    <div className="space-y-4">
      <AddSourceForm onAdded={load} />

      {loading && <p className="text-gray-500 text-sm p-6">Lädt…</p>}

      {!loading && sources.length === 0 && (
        <div className="bg-white border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">Noch keine Quellen. Füge oben Wettbewerber-Profile hinzu.</p>
        </div>
      )}

      <div className="space-y-2">
        {sources.map(s => {
          const Icon = PLATFORM_ICONS[s.platform];
          const isCrawling = crawlingId === s.id;
          const result = crawlResult?.id === s.id ? crawlResult : null;
          return (
            <div key={s.id} className="bg-white border border-gray-200 p-3 md:p-4">
              <div className="flex items-start gap-3">
                <div className="bg-[#0a0a0a] text-[#befa0a] p-2 flex-shrink-0">
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-sm truncate" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {s.display_name || s.profile_url.split('/').filter(Boolean).pop()}
                    </p>
                    {!s.active && <span className="text-[10px] text-gray-400 uppercase">inaktiv</span>}
                  </div>
                  <a href={s.profile_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-gray-500 truncate block hover:text-[#658a07]">
                    {s.profile_url}
                  </a>
                  {s.last_crawled_at && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      Zuletzt gecrawlt: {new Date(s.last_crawled_at).toLocaleString('de-DE')}
                    </p>
                  )}
                  {result && (
                    <div className={`mt-2 text-xs ${result.error ? 'text-red-600' : 'text-emerald-700'}`}>
                      {result.error || `✓ ${result.inserted} neu, ${result.skipped} übersprungen`}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => crawl(s.id)} disabled={isCrawling}
                    className="p-1.5 hover:bg-[#befa0a]/20 disabled:opacity-50" title="Crawlen">
                    <RefreshCw size={16} className={isCrawling ? 'animate-spin text-[#658a07]' : 'text-gray-600'} />
                  </button>
                  <button onClick={() => remove(s.id)} className="p-1.5 hover:bg-red-50 text-gray-400 hover:text-red-500" title="Löschen">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Tab 2: Insights (Top-Posts) ─────────────────────────────────────────────
function InsightsTab() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platform, setPlatform] = useState('all');
  const [adaptingId, setAdaptingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: 'engagement', limit: 50 };
      if (platform !== 'all') params.platform = platform;
      const { data } = await axios.get(`${API}/content/posts`, { headers: authHeaders(), params });
      setPosts(data || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [platform]);

  useEffect(() => { load(); }, [load]);

  const adapt = async (postId, targetPlatform) => {
    setAdaptingId(postId);
    try {
      const { data } = await axios.post(`${API}/content/posts/${postId}/adapt`, null,
        { headers: authHeaders(), params: { target_platform: targetPlatform } });
      alert(`Draft erstellt: ${data.hook?.slice(0, 60)}…`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Adaption fehlgeschlagen');
    }
    setAdaptingId(null);
  };

  return (
    <div className="space-y-4">
      {/* Platform filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['all', 'linkedin', 'instagram', 'youtube'].map(p => (
          <button key={p} onClick={() => setPlatform(p)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex-shrink-0 transition-colors ${
              platform === p ? 'bg-[#0a0a0a] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
            }`}
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {p === 'all' ? 'Alle' : PLATFORM_LABELS[p]}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-500 text-sm p-6">Lädt…</p>}

      {!loading && posts.length === 0 && (
        <div className="bg-white border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">Noch keine Posts. Crawle erst Quellen im Tab "Quellen".</p>
        </div>
      )}

      <div className="space-y-2">
        {posts.map(p => {
          const Icon = PLATFORM_ICONS[p.platform];
          const expanded = expandedId === p.id;
          const text = p.text_content || p.title || '';
          return (
            <div key={p.id} className="bg-white border border-gray-200 p-3 md:p-4">
              <div className="flex items-start gap-3 mb-2">
                <div className="bg-[#0a0a0a] text-[#befa0a] p-1.5 flex-shrink-0">
                  <Icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                  {p.hook && (
                    <p className="font-bold text-sm mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {p.hook}
                    </p>
                  )}
                  <p className={`text-xs md:text-sm text-gray-700 whitespace-pre-line ${expanded ? '' : 'line-clamp-3'}`}>
                    {text}
                  </p>
                  {text.length > 200 && (
                    <button onClick={() => setExpandedId(expanded ? null : p.id)}
                      className="text-[10px] text-[#658a07] uppercase tracking-wider mt-1 flex items-center gap-1">
                      {expanded ? <><ChevronUp size={12} /> einklappen</> : <><ChevronDown size={12} /> mehr</>}
                    </button>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-3 text-xs text-gray-500 mb-2">
                {p.engagement_likes > 0 && <span className="flex items-center gap-1"><Heart size={12} /> {p.engagement_likes}</span>}
                {p.engagement_comments > 0 && <span className="flex items-center gap-1"><MessageCircle size={12} /> {p.engagement_comments}</span>}
                {p.engagement_views > 0 && <span className="flex items-center gap-1"><Eye size={12} /> {p.engagement_views}</span>}
                {p.posted_at && <span>{new Date(p.posted_at).toLocaleDateString('de-DE')}</span>}
              </div>

              {/* Claude analysis */}
              {(p.topic || p.structure || p.performance_analysis) && expanded && (
                <div className="bg-gray-50 border-l-2 border-[#befa0a] px-3 py-2 mb-2 text-xs space-y-1">
                  {p.topic && <p><span className="font-bold uppercase tracking-wider text-[10px]">Thema:</span> {p.topic}</p>}
                  {p.structure && <p><span className="font-bold uppercase tracking-wider text-[10px]">Struktur:</span> {p.structure}</p>}
                  {p.performance_analysis && <p><span className="font-bold uppercase tracking-wider text-[10px]">Warum:</span> {p.performance_analysis}</p>}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {p.external_url && (
                  <a href={p.external_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs px-2 py-1 border border-gray-200 text-gray-600 hover:border-gray-400 flex items-center gap-1">
                    <ExternalLink size={11} /> Original
                  </a>
                )}
                <button onClick={() => adapt(p.id, 'linkedin')} disabled={adaptingId === p.id}
                  className="text-xs px-2 py-1 bg-[#befa0a] text-[#0a0a0a] font-bold uppercase tracking-wider hover:bg-white border border-[#befa0a] disabled:opacity-50 flex items-center gap-1"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <Sparkles size={11} /> LinkedIn-Draft
                </button>
                <button onClick={() => adapt(p.id, 'instagram')} disabled={adaptingId === p.id}
                  className="text-xs px-2 py-1 bg-white text-[#0a0a0a] font-bold uppercase tracking-wider hover:bg-[#befa0a]/10 border border-[#0a0a0a] disabled:opacity-50 flex items-center gap-1"
                  style={{ fontFamily: "'Oswald', sans-serif" }}>
                  <Sparkles size={11} /> Instagram-Draft
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Tab 3: Entwürfe (Drafts) ─────────────────────────────────────────────────
function DraftsTab() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (filterStatus !== 'all') params.status = filterStatus;
      const { data } = await axios.get(`${API}/content/drafts`, { headers: authHeaders(), params });
      setDrafts(data || []);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (d) => {
    setEditingId(d.id);
    setEditForm({
      hook: d.hook || '',
      text_content: d.text_content || '',
      hashtags: d.hashtags || '',
      scheduled_for_local: isoToLocalInput(d.scheduled_for),
      _current_status: d.status,
    });
  };

  const saveEdit = async () => {
    setBusyId(editingId);
    const payload = {
      hook: editForm.hook,
      text_content: editForm.text_content,
      hashtags: editForm.hashtags,
    };
    const iso = localInputToISO(editForm.scheduled_for_local);
    if (iso) {
      payload.scheduled_for = iso;
      // Wenn Status noch 'draft' war und ein Datum gesetzt wird → auto auf 'scheduled'
      if (editForm._current_status === 'draft') payload.status = 'scheduled';
    } else if (editForm.scheduled_for_local === '') {
      payload.scheduled_for = null;
    }
    await axios.patch(`${API}/content/drafts/${editingId}`, payload, { headers: authHeaders() });
    setEditingId(null);
    setBusyId(null);
    load();
  };

  const copyText = async (d) => {
    try {
      await navigator.clipboard.writeText(buildDraftText(d));
      alert('Text kopiert. Auf LinkedIn/Instagram einfügen.');
    } catch (e) {
      alert('Kopieren fehlgeschlagen. Markiere den Text manuell.');
    }
  };

  const openPlatform = (platform) => {
    const url = platform === 'linkedin'
      ? 'https://www.linkedin.com/feed/'
      : 'https://www.instagram.com/';
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const setStatus = async (id, status) => {
    setBusyId(id);
    await axios.patch(`${API}/content/drafts/${id}`, { status }, { headers: authHeaders() });
    setBusyId(null);
    load();
  };

  const generateHeygen = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/content/drafts/${id}/heygen`, {}, { headers: authHeaders() });
      alert('Heygen-Video wird generiert. Status erscheint in 1-5 Minuten.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Heygen fehlgeschlagen');
    }
    setBusyId(null);
    load();
  };

  const checkHeygenStatus = async (id) => {
    setBusyId(id);
    await axios.get(`${API}/content/drafts/${id}/heygen/status`, { headers: authHeaders() });
    setBusyId(null);
    load();
  };

  const markPosted = async (id) => {
    const url = window.prompt('URL des geposteten Beitrags (optional):');
    setBusyId(id);
    await axios.post(`${API}/content/drafts/${id}/mark-posted`, null,
      { headers: authHeaders(), params: url ? { posted_url: url } : {} });
    setBusyId(null);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Draft löschen?')) return;
    await axios.delete(`${API}/content/drafts/${id}`, { headers: authHeaders() });
    load();
  };

  return (
    <div className="space-y-4">
      {/* Status filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {['all', 'draft', 'approved', 'scheduled', 'posted', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider flex-shrink-0 transition-colors ${
              filterStatus === s ? 'bg-[#0a0a0a] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
            }`}
            style={{ fontFamily: "'Oswald', sans-serif" }}>
            {s === 'all' ? 'Alle' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {loading && <p className="text-gray-500 text-sm p-6">Lädt…</p>}

      {!loading && drafts.length === 0 && (
        <div className="bg-white border border-gray-200 p-8 text-center">
          <p className="text-gray-500 text-sm">Keine Drafts. Adaptiere Posts im Tab "Insights".</p>
        </div>
      )}

      <div className="space-y-2">
        {drafts.map(d => {
          const Icon = PLATFORM_ICONS[d.platform];
          const isEditing = editingId === d.id;
          const isBusy = busyId === d.id;
          return (
            <div key={d.id} className="bg-white border border-gray-200 p-3 md:p-4">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <div className="bg-[#0a0a0a] text-[#befa0a] p-1.5 flex-shrink-0">
                    <Icon size={14} />
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-700'}`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    {STATUS_LABELS[d.status] || d.status}
                  </span>
                  {d.heygen_status && (
                    <span className={`text-[10px] uppercase tracking-wider flex items-center gap-1 ${
                      d.heygen_status === 'completed' ? 'text-emerald-600' :
                      d.heygen_status === 'failed' ? 'text-red-500' : 'text-yellow-600'
                    }`}>
                      <Video size={11} /> {d.heygen_status}
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-gray-400">
                  {d.created_at && new Date(d.created_at).toLocaleDateString('de-DE')}
                </div>
              </div>

              {isEditing ? (
                <div className="space-y-2 mb-3">
                  <input value={editForm.hook} onChange={e => setEditForm({ ...editForm, hook: e.target.value })}
                    placeholder="Hook"
                    className="w-full border border-gray-300 px-3 py-2 text-sm font-bold focus:outline-none focus:border-[#befa0a]" />
                  <textarea value={editForm.text_content} onChange={e => setEditForm({ ...editForm, text_content: e.target.value })}
                    rows={8} placeholder="Post-Text"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
                  <input value={editForm.hashtags} onChange={e => setEditForm({ ...editForm, hashtags: e.target.value })}
                    placeholder="#hashtags"
                    className="w-full border border-gray-300 px-3 py-2 text-xs focus:outline-none focus:border-[#befa0a]" />
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      Geplant für (optional)
                    </label>
                    <div className="flex items-center gap-2">
                      <CalendarClock size={14} className="text-gray-400 flex-shrink-0" />
                      <input type="datetime-local" value={editForm.scheduled_for_local || ''}
                        onChange={e => setEditForm({ ...editForm, scheduled_for_local: e.target.value })}
                        className="flex-1 border border-gray-300 px-3 py-2 text-xs focus:outline-none focus:border-[#befa0a]" />
                      {editForm.scheduled_for_local && (
                        <button onClick={() => setEditForm({ ...editForm, scheduled_for_local: '' })}
                          className="text-gray-400 hover:text-red-500 p-1" title="Datum löschen">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">
                      Wenn gesetzt: Status springt auto auf "Geplant" und Draft erscheint im Kalender-Tab.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingId(null)}
                      className="flex-1 py-2 border border-gray-300 text-gray-700 text-xs font-bold uppercase tracking-wider hover:bg-gray-50"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>Abbrechen</button>
                    <button onClick={saveEdit} disabled={isBusy}
                      className="flex-1 py-2 bg-[#befa0a] text-[#0a0a0a] text-xs font-bold uppercase tracking-wider hover:bg-white disabled:opacity-50"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>Speichern</button>
                  </div>
                </div>
              ) : (
                <>
                  {d.hook && (
                    <p className="font-bold text-sm mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {d.hook}
                    </p>
                  )}
                  <p className="text-xs md:text-sm text-gray-700 whitespace-pre-line line-clamp-4 mb-2">
                    {d.text_content}
                  </p>
                  {d.hashtags && <p className="text-xs text-[#658a07] mb-2">{d.hashtags}</p>}
                  {d.scheduled_for && (
                    <p className="text-xs text-yellow-700 mb-2 flex items-center gap-1">
                      <CalendarClock size={11} /> Geplant: {new Date(d.scheduled_for).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
                    </p>
                  )}
                  {d.heygen_video_url && (
                    <a href={d.heygen_video_url} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-[#658a07] hover:text-[#0a0a0a] mb-2">
                      <Video size={12} /> Video ansehen
                    </a>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-1.5">
                    <button onClick={() => startEdit(d)}
                      className="text-xs px-2 py-1 border border-gray-200 hover:border-gray-400 flex items-center gap-1">
                      <Edit3 size={11} /> Edit
                    </button>
                    <button onClick={() => copyText(d)}
                      className="text-xs px-2 py-1 border border-gray-300 hover:border-[#befa0a] hover:bg-[#befa0a]/10 flex items-center gap-1"
                      title="Hook + Text + Hashtags in Zwischenablage">
                      <Copy size={11} /> Kopieren
                    </button>
                    {d.platform === 'linkedin' && (
                      <button onClick={() => openPlatform('linkedin')}
                        className="text-xs px-2 py-1 border border-[#0a66c2] text-[#0a66c2] hover:bg-[#0a66c2] hover:text-white flex items-center gap-1"
                        title="LinkedIn-Feed öffnen">
                        <Linkedin size={11} /> LinkedIn
                      </button>
                    )}
                    {d.platform === 'instagram' && (
                      <button onClick={() => openPlatform('instagram')}
                        className="text-xs px-2 py-1 border border-pink-500 text-pink-600 hover:bg-pink-500 hover:text-white flex items-center gap-1"
                        title="Instagram öffnen">
                        <Instagram size={11} /> Instagram
                      </button>
                    )}
                    {d.status === 'draft' && (
                      <button onClick={() => setStatus(d.id, 'approved')} disabled={isBusy}
                        className="text-xs px-2 py-1 bg-blue-500 text-white font-bold uppercase tracking-wider hover:bg-blue-600 flex items-center gap-1"
                        style={{ fontFamily: "'Oswald', sans-serif" }}>
                        <Check size={11} /> Freigeben
                      </button>
                    )}
                    {(!d.heygen_video_id || d.heygen_status === 'failed') && (
                      <button onClick={() => generateHeygen(d.id)} disabled={isBusy}
                        className="text-xs px-2 py-1 bg-[#0a0a0a] text-[#befa0a] font-bold uppercase tracking-wider hover:bg-[#1f1f1f] flex items-center gap-1"
                        style={{ fontFamily: "'Oswald', sans-serif" }}>
                        <Video size={11} /> {d.heygen_status === 'failed' ? 'Heygen Retry' : 'Heygen'}
                      </button>
                    )}
                    {d.heygen_video_id && d.heygen_status !== 'completed' && (
                      <button onClick={() => checkHeygenStatus(d.id)} disabled={isBusy}
                        className="text-xs px-2 py-1 border border-yellow-400 text-yellow-700 hover:bg-yellow-50 flex items-center gap-1">
                        <RefreshCw size={11} className={isBusy ? 'animate-spin' : ''} /> Status
                      </button>
                    )}
                    {d.status !== 'posted' && (
                      <button onClick={() => markPosted(d.id)} disabled={isBusy}
                        className="text-xs px-2 py-1 bg-emerald-500 text-white font-bold uppercase tracking-wider hover:bg-emerald-600 flex items-center gap-1"
                        style={{ fontFamily: "'Oswald', sans-serif" }}>
                        <Send size={11} /> Gepostet
                      </button>
                    )}
                    {d.posted_url && (
                      <a href={d.posted_url} target="_blank" rel="noopener noreferrer"
                        className="text-xs px-2 py-1 border border-gray-200 text-gray-600 hover:border-gray-400 flex items-center gap-1">
                        <ExternalLink size={11} /> Live
                      </a>
                    )}
                    <button onClick={() => remove(d.id)} disabled={isBusy}
                      className="text-xs px-2 py-1 text-red-400 hover:text-red-600 hover:bg-red-50 ml-auto flex items-center gap-1">
                      <Trash2 size={11} />
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Tab 4: Kalender (Scheduled) ──────────────────────────────────────────────
function CalendarTab() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/content/drafts`, {
        headers: authHeaders(),
        params: { status: 'scheduled', limit: 100 },
      });
      setDrafts((data || []).sort((a, b) => new Date(a.scheduled_for) - new Date(b.scheduled_for)));
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group by date
  const byDate = drafts.reduce((acc, d) => {
    if (!d.scheduled_for) return acc;
    const key = new Date(d.scheduled_for).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long' });
    if (!acc[key]) acc[key] = [];
    acc[key].push(d);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {loading && <p className="text-gray-500 text-sm p-6">Lädt…</p>}

      {!loading && Object.keys(byDate).length === 0 && (
        <div className="bg-white border border-gray-200 p-8 text-center">
          <Calendar size={32} className="text-gray-300 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">Keine geplanten Posts.</p>
          <p className="text-gray-400 text-xs mt-1">Setze in Drafts ein Schedule-Datum, dann erscheinen sie hier.</p>
        </div>
      )}

      {Object.entries(byDate).map(([dateLabel, items]) => (
        <div key={dateLabel} className="bg-white border border-gray-200">
          <div className="bg-[#0a0a0a] text-white px-4 py-2 flex items-center gap-2">
            <Calendar size={14} className="text-[#befa0a]" />
            <p className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>
              {dateLabel}
            </p>
          </div>
          <div className="divide-y divide-gray-100">
            {items.map(d => {
              const Icon = PLATFORM_ICONS[d.platform];
              return (
                <div key={d.id} className="p-3 flex items-start gap-3">
                  <div className="bg-gray-100 text-[#0a0a0a] p-1.5 flex-shrink-0">
                    <Icon size={12} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 mb-0.5 flex items-center gap-1">
                      <Clock size={10} /> {new Date(d.scheduled_for).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                    {d.hook && <p className="font-bold text-sm" style={{ fontFamily: "'Oswald', sans-serif" }}>{d.hook}</p>}
                    <p className="text-xs text-gray-600 line-clamp-2 mt-1">{d.text_content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}


// ── Tab 5: Brand (Voice + Heygen-Defaults) ───────────────────────────────────
function BrandTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/content/brand`, { headers: authHeaders() })
      .then(({ data }) => { setData(data); setDirty(false); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setField = (key, value) => {
    setData(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    // Nur die editierbaren Felder schicken
    const payload = {
      who_you_are: data.who_you_are,
      three_words: data.three_words,
      voice_description: data.voice_description,
      do_words: data.do_words,
      dont_words: data.dont_words,
      hook_patterns: data.hook_patterns,
      cta_style: data.cta_style,
      hashtags_linkedin: data.hashtags_linkedin,
      hashtags_instagram: data.hashtags_instagram,
      heygen_avatar_id: data.heygen_avatar_id,
      heygen_voice_id: data.heygen_voice_id,
      heygen_background_color: data.heygen_background_color,
      heygen_dimension_width: data.heygen_dimension_width ? Number(data.heygen_dimension_width) : null,
      heygen_dimension_height: data.heygen_dimension_height ? Number(data.heygen_dimension_height) : null,
      heygen_background_image_url: data.heygen_background_image_url,
      heygen_template_id: data.heygen_template_id,
      example_posts: data.example_posts,
    };
    try {
      const { data: updated } = await axios.patch(`${API}/content/brand`, payload, { headers: authHeaders() });
      setData(updated);
      setDirty(false);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
    setSaving(false);
  };

  if (loading || !data) return <p className="text-gray-500 text-sm p-6">Lädt…</p>;

  const Field = ({ label, hint, children }) => (
    <div className="bg-white border border-gray-200 p-4">
      <label className="block text-xs font-bold uppercase tracking-wider text-[#1f1f1f] mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
        {label}
      </label>
      {hint && <p className="text-[11px] text-gray-500 mb-2">{hint}</p>}
      {children}
    </div>
  );

  return (
    <div className="space-y-4 pb-24">
      {/* Sticky Save Bar */}
      <div className="sticky top-0 z-10 bg-[#f5f5f5] -mx-3 md:-mx-6 px-3 md:px-6 py-3 border-b border-gray-200 flex items-center justify-between gap-2">
        <p className="text-xs text-gray-500">
          {dirty ? '● Ungespeicherte Änderungen' : savedAt ? '✓ Gespeichert' : 'Alle Felder optional — leer = wird nicht in Prompt einbezogen'}
        </p>
        <button onClick={save} disabled={!dirty || saving}
          className="px-4 py-2 bg-[#befa0a] text-[#0a0a0a] text-xs font-bold uppercase tracking-wider hover:bg-white disabled:opacity-40 flex items-center gap-1"
          style={{ fontFamily: "'Oswald', sans-serif" }}>
          <Save size={12} /> {saving ? 'Speichert…' : 'Speichern'}
        </button>
      </div>

      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#658a07]" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Voice & Identität
      </p>

      <Field label="Wer du bist" hint="1 Absatz — Wer ist EnBet, wer ist Sebastian, an wen richtet sich der Content?">
        <textarea value={data.who_you_are || ''} onChange={e => setField('who_you_are', e.target.value)}
          rows={3} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <Field label="3 Worte" hint="Drei Adjektive die EnBet beschreiben (z.B. 'direkt, pragmatisch, deutsch')">
        <input value={data.three_words || ''} onChange={e => setField('three_words', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <Field label="Tonalität / Voice-Description" hint="2-3 Sätze: Wie klingt EnBet? Mit Beispielsätzen.">
        <textarea value={data.voice_description || ''} onChange={e => setField('voice_description', e.target.value)}
          rows={4} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#658a07] pt-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Wording — Do's & Don'ts
      </p>

      <Field label="Wörter die du aktiv nutzt" hint="Komma-getrennt. Werden vom Agent bevorzugt eingebaut.">
        <textarea value={data.do_words || ''} onChange={e => setField('do_words', e.target.value)}
          rows={2} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <Field label="Verbotene Wörter" hint="Komma-getrennt. Agent wird sie NIE verwenden (z.B. 'synergetisch, Game-Changer').">
        <textarea value={data.dont_words || ''} onChange={e => setField('dont_words', e.target.value)}
          rows={2} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#658a07] pt-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Hooks & CTAs
      </p>

      <Field label="Hook-Patterns" hint="3-5 deiner Lieblings-Hook-Strukturen (1 pro Zeile).">
        <textarea value={data.hook_patterns || ''} onChange={e => setField('hook_patterns', e.target.value)}
          rows={5} placeholder={`Ich habe X gemacht und Y ist passiert.\nDie meisten denken X — falsch.\nVor 6 Monaten dachte ich noch X. Heute…`}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <Field label="CTA-Stil" hint="Wie endet ein Post? (Soft-Frage / Direct-CTA / Kein-CTA / etc.)">
        <input value={data.cta_style || ''} onChange={e => setField('cta_style', e.target.value)}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#658a07] pt-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Hashtag-Pools
      </p>

      <Field label="LinkedIn-Hashtags" hint="Komma- oder Leerzeichen-getrennt. Agent wählt daraus 5-7 passende aus.">
        <textarea value={data.hashtags_linkedin || ''} onChange={e => setField('hashtags_linkedin', e.target.value)}
          rows={2} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <Field label="Instagram-Hashtags" hint="Eigener Pool für Instagram (oft andere Tags als LinkedIn).">
        <textarea value={data.hashtags_instagram || ''} onChange={e => setField('hashtags_instagram', e.target.value)}
          rows={2} className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#658a07] pt-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Heygen Avatar-Defaults
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Avatar-ID" hint="Aus Heygen Studio → Avatar wählen → ID kopieren">
          <input value={data.heygen_avatar_id || ''} onChange={e => setField('heygen_avatar_id', e.target.value)}
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
        </Field>
        <Field label="Voice-ID" hint="Aus Heygen → Voice → 'default' falls unklar">
          <input value={data.heygen_voice_id || ''} onChange={e => setField('heygen_voice_id', e.target.value)}
            placeholder="default"
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
        </Field>
        <Field label="Hintergrundfarbe" hint="Hex z.B. #0a0a0a (EnBet-Schwarz)">
          <input value={data.heygen_background_color || ''} onChange={e => setField('heygen_background_color', e.target.value)}
            placeholder="#0a0a0a"
            className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
        </Field>
        <Field label="Dimensionen (B x H)" hint="z.B. 1080x1920 (Reel/Story), 1920x1080 (Landscape)">
          <div className="flex items-center gap-2">
            <input type="number" value={data.heygen_dimension_width || ''} onChange={e => setField('heygen_dimension_width', e.target.value)}
              placeholder="1080"
              className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
            <span className="text-gray-400">×</span>
            <input type="number" value={data.heygen_dimension_height || ''} onChange={e => setField('heygen_dimension_height', e.target.value)}
              placeholder="1920"
              className="flex-1 border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
        </Field>
      </div>

      <Field label="Background-Image URL (alternativ zu Farbe)" hint="Öffentliches PNG/JPG mit Logo + Brand-Look (1080×1920 für Reels). Wenn gesetzt, hat Vorrang vor Hintergrundfarbe.">
        <input value={data.heygen_background_image_url || ''} onChange={e => setField('heygen_background_image_url', e.target.value)}
          placeholder="https://www.enbet-consulting.de/heygen-bg.png"
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <Field label="Heygen-Template ID (Studio-Vorlage, optional)" hint="Wenn du in Heygen Studio ein Template mit Logo + Layout gebaut hast: ID hier eintragen — hat höchste Priorität.">
        <input value={data.heygen_template_id || ''} onChange={e => setField('heygen_template_id', e.target.value)}
          placeholder="z.B. abc123-template-id"
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>

      <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#658a07] pt-2" style={{ fontFamily: "'Oswald', sans-serif" }}>
        Style-Anker
      </p>

      <Field label="Beispiel-Posts" hint="3-5 deiner best-performenden eigenen Posts (1 pro Zeile bzw. Block). Dient als Style-Reference für Claude.">
        <textarea value={data.example_posts || ''} onChange={e => setField('example_posts', e.target.value)}
          rows={8} placeholder={`Beispiel-Post 1 hier einfügen...\n\n---\n\nBeispiel-Post 2...`}
          className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-[#befa0a]" />
      </Field>
    </div>
  );
}


// ── Main Component ───────────────────────────────────────────────────────────
export default function ContentAgent({ inline }) {
  const [activeTab, setActiveTab] = useState('sources');
  const [dashboard, setDashboard] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/dashboard`, { headers: authHeaders() })
      .then(({ data }) => setDashboard(data))
      .catch(() => {});
  }, [activeTab]);

  const tabs = [
    { id: 'sources', label: 'Quellen', icon: TrendingUp },
    { id: 'insights', label: 'Insights', icon: Sparkles },
    { id: 'drafts', label: 'Entwürfe', icon: FileText },
    { id: 'calendar', label: 'Kalender', icon: Calendar },
    { id: 'brand', label: 'Brand', icon: Palette },
  ];

  return (
    <div className={inline ? "p-3 md:p-6 max-w-5xl mx-auto w-full" : "min-h-screen bg-[#f5f5f5] p-3 md:p-6"}>
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <p className="text-[#658a07] text-xs uppercase tracking-[0.3em] font-bold mb-1" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Content-Agent
        </p>
        <h1 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: "'Oswald', sans-serif" }}>
          Wettbewerb beobachten · Adaptieren · Posten
        </h1>
      </div>

      {/* Dashboard mini-stats */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 mb-4 md:mb-6">
          <div className="bg-white border border-gray-200 p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Quellen</p>
            <p className="text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{dashboard.sources_active}</p>
          </div>
          <div className="bg-white border border-gray-200 p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Posts</p>
            <p className="text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{dashboard.posts_total}</p>
          </div>
          <div className="bg-white border border-gray-200 p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Drafts</p>
            <p className="text-2xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>{dashboard.drafts_pending}</p>
          </div>
          <div className="bg-white border border-gray-200 p-3">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>Gepostet</p>
            <p className="text-2xl font-bold text-emerald-600" style={{ fontFamily: "'Oswald', sans-serif" }}>{dashboard.drafts_posted}</p>
          </div>
        </div>
      )}

      {/* Tab pills */}
      <div className="flex gap-1 mb-4 md:mb-5 overflow-x-auto pb-1">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = activeTab === t.id;
          return (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-3 md:px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 flex-shrink-0 transition-colors ${
                active ? 'bg-[#0a0a0a] text-[#befa0a]' : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400'
              }`}
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Icon size={12} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'sources' && <SourcesTab />}
      {activeTab === 'insights' && <InsightsTab />}
      {activeTab === 'drafts' && <DraftsTab />}
      {activeTab === 'calendar' && <CalendarTab />}
      {activeTab === 'brand' && <BrandTab />}
    </div>
  );
}
